import React, { createContext, useContext, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Drawer } from "@mui/material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { Logo } from "../../assets/images";
import {
  faHome,
  faChartPie,
  faGraduationCap,
  faAnglesLeft,
  faAnglesRight,
  faUserGear,
  faUsersGear,
  faUserGraduate,
  faChalkboardTeacher,
  faBookOpen,
  faFolderOpen,
  faCalendarAlt,
  faComments,
  faEnvelopeOpenText,
  faCreditCard,
  faChartBar,
  faSignOutAlt,
  faSquareCaretDown,
  faCircleInfo,
  faCoins,
  faTicket,
  faCrown,
  faBolt,
  faChartLine,
  faHeadset,
  faClipboardCheck,
  faCommentDots
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faInstagram,
  faYoutube
} from "@fortawesome/free-brands-svg-icons";

// CONTEXT
const SidebarContext = createContext<{ expanded: boolean }>({ expanded: true });

// Types
interface SidebarLink {
  path: string;
  name: string;
  roles: string[];
  requiredRoleId?: number;
}

interface SidebarProps {
  className?: string;
  expanded?: boolean;
  onToggle?: () => void;
}

// Sidebar configuration
const sidebarLinks: SidebarLink[] = [
  { path: "/home", name: "Mes Classes", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER", "ROLE_TEACHER", "ROLE_STUDENT"] },
  { path: "/offer-teacher", name: "Offre Professeurs", roles: ["ROLE_SUPER_TEACHER", "ROLE_ADMIN"] },
  { path: "/offer-student", name: "Offres", roles: ["ROLE_STUDENT", "ROLE_ADMIN"] },
  { path: "/subscription", name: "Abonnement", roles: ["ROLE_SUPER_TEACHER", "ROLE_STUDENT"] },
  { path: "/management-prof", name: "Mes Professeurs", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER"] },
  { path: "/management-student", name: "Mes Élèves", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER"] },
  { path: "/management-users", name: "Gestion des utilisateurs", roles: ["ROLE_ADMIN"] },
  { path: "/promo-codes", name: "Gestion Codes Promo", roles: ["ROLE_ADMIN"] },
  // { path: "/files", name: "Fichiers", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER", "ROLE_TEACHER"] },
  { path: "/calender", name: "Calendrier Live", roles: ["ROLE_ADMIN", "ROLE_TEACHER", "ROLE_SUPER_TEACHER", "ROLE_STUDENT"] },
  { path: "/chat", name: "Chat Room", roles: ["ROLE_ADMIN", "ROLE_TEACHER", "ROLE_SUPER_TEACHER", "ROLE_STUDENT"] },
  { path: "/requests-prof", name: "Demandes des Professeurs", roles: ["ROLE_ADMIN"] },
  { path: "/requests-student", name: "Demandes des Élèves", roles: ["ROLE_ADMIN"] },
  { path: "/stats", name: "Statistiques", roles: ["ROLE_ADMIN"] },
  { path: "/referral-code", name: "Code Promo", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER", "ROLE_TEACHER", "ROLE_STUDENT"] },
  { path: "/support", name: "Support", roles: ["ROLE_ADMIN", "ROLE_SUPER_TEACHER", "ROLE_TEACHER", "ROLE_STUDENT"] },
];

// Function to get icon by text
function getIconByText(text: string) {
  const lower = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  // Specific match first
  if (lower.includes("gestion codes promo")) return faTicket;
  if (lower.includes("code promo") || lower.includes("promo")) return faCoins;
  if (lower.includes("gestion des utilisateurs")) return faUsersGear;
  if (lower.includes("mes professeurs") || lower.includes("management-prof")) return faChalkboardTeacher;
  if (lower.includes("mes eleves") || lower.includes("etudiant") || lower.includes("eleve") || lower.includes("élève")) return faUserGraduate;
  if (lower.includes("demande")) return faClipboardCheck;
  
  // Categorical matches
  if (lower === "mes classes" || lower === "mes matieres") return faGraduationCap;
  if (lower === "accueil") return faHome;
  if (lower.includes("tableau de bord")) return faChartPie;
  if (lower.includes("abonnement")) return faCrown;
  if (lower.includes("statistique")) return faChartLine;
  if (lower.includes("cours")) return faBookOpen;
  if (lower.includes("fichier")) return faFolderOpen;
  if (lower.includes("calendrier")) return faCalendarAlt;
  if (lower.includes("chat")) return faCommentDots;
  if (lower.includes("support")) return faHeadset;
  if (lower.includes("gestion")) return faUserGear;
  if (lower.includes("offre")) return faBolt;
  
  return faGraduationCap;
}

// Sidebar Item Component
function SidebarItem({
  text,
  to,
  alert,
}: {
  text: string;
  to: string;
  alert?: boolean;
}) {
  const { expanded } = useContext(SidebarContext);
  const icon = getIconByText(text);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const itemRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (!expanded && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.right + 24, // 24px offset from the right edge
        y: rect.top + rect.height / 2, // Center vertically
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <NavLink to={to}>
      {({ isActive }) => (
        <li className={`relative group`}>
          <div
            ref={itemRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`
              relative flex items-center w-full
              ${expanded ? "px-4 py-2.5 gap-3.5" : "px-3 py-2.5 justify-center"}
              rounded-2xl font-semibold cursor-pointer
              transition-all duration-500
              ${isActive
                ? "bg-primary text-white shadow-[0_15px_35px_-8px_rgba(9,116,95,0.45)] transform scale-[1.02]"
                : "text-slate-600 hover:bg-white hover:shadow-xl hover:shadow-gray-200/40 hover:text-primary"
              }
            `}
          >
            {/* Icon Housing */}
            <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center transition-all duration-500 ${
                isActive 
                  ? "bg-white/20 text-white shadow-inner" 
                  : "bg-gray-100/70 text-slate-400 group-hover:bg-primary group-hover:text-white group-hover:shadow-lg group-hover:shadow-primary/20"
              }`}>
              <FontAwesomeIcon icon={icon} className={`${expanded ? "text-[16px]" : "text-[20px]"}`} />
            </div>
          
            <span
              className={`font-black uppercase text-[10px] tracking-[0.15em] transition-all duration-500 ease-out whitespace-nowrap ${expanded
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10 w-0 pointer-events-none overflow-hidden"
                }`}
            >
              {text}
            </span>

            {/* Alert Badge */}
            {alert && expanded && (
              <div className="w-2 h-2 ml-auto bg-red-500 rounded-full"></div>
            )}
            {alert && !expanded && (
              <div className="absolute w-3 h-3 bg-red-500 border-2 border-white rounded-full -top-1 -right-1"></div>
            )}
          </div>

          {/* Tooltip Portal - renders outside sidebar */}
          {!expanded && showTooltip && createPortal(
            <div
              className="fixed px-4 py-2.5 text-white text-sm font-medium rounded-lg shadow-2xl transition-all duration-150 whitespace-nowrap pointer-events-none"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                transform: 'translateY(-50%)',
                backgroundColor: '#1f2937',
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                zIndex: 99999,
              }}
            >
              {text}
              {/* Arrow pointing left */}
              <div
                className="absolute w-0 h-0 -translate-y-1/2 right-full top-1/2"
                style={{
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderRight: '6px solid #1f2937',
                }}
              />
            </div>,
            document.body
          )}
        </li>
      )}
    </NavLink>
  );
}

// Main Sidebar Component
const Sidebar: React.FC<SidebarProps> = ({ className = "", expanded = true, onToggle }) => {
  const role = useSelector((state: RootState) => state?.user?.userData?.role?.name);
  const roleId = useSelector((state: RootState) => state?.user?.userData?.role?.id);
  const userData = useSelector((state: RootState) => state.user.userData);
  const isLogged = !!role;
  const [dropdownMobileOpen, setDropdownMobileOpen] = useState(false);
  const navigation = require('react-router-dom').useNavigate();
  const userFullName = userData?.fullName || '';
  const userInitials = userFullName
    ? userFullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : '';
  const handleLogout = () => {
    navigation("/");
    localStorage.clear();
    setDropdownMobileOpen(false);
    setIsMobileSidebarOpen(false);
  };
  const goToDashboard = () => {
    const path = role === "ROLE_TEACHER" ? "/subject" : "/home";
    navigation(path);
    setDropdownMobileOpen(false);
    setIsMobileSidebarOpen(false);
  };
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);

  // Custom scrollbar styles
  React.useEffect(() => {
    const styleId = 'sidebar-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      const customScrollbarStyle = `
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
        .custom-scrollbar-hidden {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          background: transparent !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar-thumb {
          display: none !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar-track {
          display: none !important;
        }
        /* Hide scrollbar for all descendants in collapsed mode */
        .custom-scrollbar-hidden, .custom-scrollbar-hidden * {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar, .custom-scrollbar-hidden *::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          background: transparent !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar-thumb, .custom-scrollbar-hidden *::-webkit-scrollbar-thumb {
          display: none !important;
        }
        .custom-scrollbar-hidden::-webkit-scrollbar-track, .custom-scrollbar-hidden *::-webkit-scrollbar-track {
          display: none !important;
        }
      `;
      styleElement.textContent = customScrollbarStyle;
      document.head.appendChild(styleElement);
    }
  }, []);
  // Listen for mobile toggle events from Header
  React.useEffect(() => {
    const handleToggle = () => setIsMobileSidebarOpen(prev => !prev);
    window.addEventListener('sidebarToggleMobile', handleToggle);
    return () => window.removeEventListener('sidebarToggleMobile', handleToggle);
  }, []);

  const getDisplayName = (name: string) => {
    if ((role === "ROLE_STUDENT" || role === "ROLE_TEACHER") && name.toLowerCase() === "mes classes") {
      return "Mes Matières";
    }
    return name;
  };

  return (
    <>
      {/* Fixed Sidebar - Desktop */}
      <aside className={`hidden lg:flex fixed left-0 top-[70px] lg:top-[80px] h-[calc(100vh-70px)] lg:h-[calc(100vh-80px)] z-40 transition-all duration-300 ease-in-out bg-white border-r border-gray-100 ${expanded ? "w-80" : "w-[120px]"} ${className}`}>
        <nav className="flex flex-col w-full h-full relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none"></div>

          {/* Navigation Links */}
          <SidebarContext.Provider value={{ expanded }}>
            <div
              className={`flex-1 ${expanded ? "px-6" : "px-4"} py-8 custom-scrollbar-hidden bg-gray-50/20 overflow-x-hidden overflow-y-auto`}
              style={{ maxHeight: '100%', scrollbarWidth: 'none', msOverflowStyle: 'none', overscrollBehavior: 'contain' }}
            >
              <ul className="space-y-3">
                {sidebarLinks.map(
                  (link) =>
                    role && link.roles.includes(role) && (!link.requiredRoleId || link.requiredRoleId === roleId) && (
                      <SidebarItem
                        key={link.path}
                        to={link.path === "/home" ? (role === "ROLE_TEACHER" ? "/subject" : link.path) : "/dashboard" + link.path}
                        text={getDisplayName(link.name)}
                      />
                    )
                )}
              </ul>
            </div>
          </SidebarContext.Provider>

          {/* Footer / Contact Section */}
          <div className={`mt-auto p-5 border-t border-gray-100/50 bg-gray-50/10 backdrop-blur-md relative z-10 ${!expanded && "flex flex-col items-center"}`}>
            {expanded ? (
              <div className="space-y-4">
                <a
                  href="https://wa.me/21651347528"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3.5 bg-white rounded-2xl border border-gray-100 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.06)] group hover:border-primary/30 hover:shadow-xl transition-all duration-500"
                >
                  <div className="w-11 h-11 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-green-200 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shrink-0">
                    <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                  </div>
                  <div className="ml-3.5 overflow-hidden">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.25em] whitespace-nowrap mb-0.5">Besoin d'aide ?</p>
                    <p className="text-[13px] font-black text-title whitespace-nowrap tracking-tight group-hover:text-primary transition-colors">+216 51 347 528</p>
                  </div>
                </a>

                <div className="flex justify-between items-center pt-2 px-4 opacity-50 hover:opacity-100 transition-opacity">
                  {[
                    { icon: faFacebook, color: "hover:text-[#1877F2]", url: "https://www.facebook.com/people/%D9%81%D9%83-%D9%86%D8%AC%D8%A7%D8%AD%D9%83/100069589923551/" },
                    { icon: faInstagram, color: "hover:text-[#E4405F]", url: "https://www.instagram.com/fok.nje7ek/" },
                    { icon: faYoutube, color: "hover:text-[#CD201F]", url: "https://www.youtube.com/@foknje7ik" }
                  ].map((social, i) => (
                    <a
                      key={i}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-slate-400 ${social.color} transition-all duration-500 hover:scale-125`}
                    >
                      <FontAwesomeIcon icon={social.icon} className="text-xl" />
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-4 py-2">
                <div className="w-12 h-12 bg-primary/5 rounded-2xl flex items-center justify-center text-primary cursor-pointer hover:bg-primary hover:text-white transition-all duration-300">
                  <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                </div>
              </div>
            )}
          </div>
        </nav>
      </aside>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={isMobileSidebarOpen}
        onClose={toggleMobileSidebar}
        className="lg:hidden"
        PaperProps={{
          sx: { width: 288, backgroundColor: 'transparent' }
        }}
      >
        <div className="h-full border-r border-gray-200 shadow-xl bg-text bg-opacity-5 backdrop-blur-xl">
          {/* Mobile Header - Same as PC */}
          <div className="flex items-center justify-between p-6 border-b border-white/20 bg-white/5 h-[80px]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center p-1.5">
                <img
                  src={Logo}
                  alt="Foknje7ek Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-black text-white leading-tight uppercase tracking-tight">
                Fok <span className="text-primary italic">Nje7ek</span>
              </span>
            </div>
            <button
              onClick={toggleMobileSidebar}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
              title="Fermer"
            >
              <FontAwesomeIcon
                icon={faAnglesLeft}
                className="w-3.5 h-3.5"
              />
            </button>
          </div>

          {/* User Profile for Mobile */}
          {isLogged && (
            <div className="px-6 py-8 border-b border-white/10 bg-white/5 backdrop-blur-md">
              <div className="flex items-center justify-between p-4 bg-white/10 rounded-3xl border border-white/10 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#09745f] to-[#07b98e] text-white rounded-2xl flex items-center justify-center text-sm font-black shadow-lg border border-white/20">
                    {userInitials}
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-black text-white truncate max-w-[120px] tracking-tight">{userFullName}</p>
                    <p className="text-[9px] font-bold text-primary uppercase tracking-widest">{role?.replace("ROLE_", "")}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setDropdownMobileOpen(!dropdownMobileOpen)}
                  className={`w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 text-white transition-transform duration-300 ${dropdownMobileOpen ? 'rotate-180' : ''}`}
                >
                  <FontAwesomeIcon icon={faSquareCaretDown} />
                </button>
              </div>

              {dropdownMobileOpen && (
                <div className="mt-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <button
                    className="flex items-center w-full px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-white/70 hover:text-white hover:bg-white/10 rounded-2xl transition-all"
                    onClick={goToDashboard}
                  >
                    <FontAwesomeIcon icon={faChartBar} className="w-4 h-4 mr-4 text-primary" />
                    Tableau de bord
                  </button>
                  <button
                    className="flex items-center w-full px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-white/70 hover:text-white hover:bg-white/10 rounded-2xl transition-all"
                    onClick={() => { navigation('/dashboard/updateprofil'); setDropdownMobileOpen(false); setIsMobileSidebarOpen(false); }}
                  >
                    <FontAwesomeIcon icon={faUserGear} className="w-4 h-4 mr-4 text-primary" />
                    Mon espace
                  </button>
                  <button
                    className="flex items-center w-full px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-rose-400 hover:text-rose-100 hover:bg-rose-500/20 rounded-2xl transition-all"
                    onClick={handleLogout}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 mr-4 text-rose-500" />
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Mobile Navigation - Same styling as PC */}
          <div className="flex-1 px-4 py-8 overflow-x-hidden overflow-y-auto custom-scrollbar-hidden bg-text bg-opacity-5"
            style={{ maxHeight: 'calc(100vh - 160px)', scrollbarWidth: 'none', msOverflowStyle: 'none', overscrollBehavior: 'contain' }}>
            <ul className="space-y-3">
              {sidebarLinks.map(
                (link) =>
                  role &&
                  link.roles.includes(role) &&
                  (!link.requiredRoleId || link.requiredRoleId === roleId) && (
                    <li key={link.path} className="relative group">
                      <NavLink
                        to={link.path === "/home" ? (role === "ROLE_TEACHER" ? "/subject" : link.path) : "/dashboard" + link.path}
                        onClick={toggleMobileSidebar}
                        className={({ isActive }) =>
                          `relative flex items-center w-full px-5 py-4 gap-4 rounded-2xl font-semibold cursor-pointer transition-all duration-300 hover:shadow-lg ${isActive
                            ? "bg-gradient-to-r from-[#09745f] to-[#07b98e] text-white shadow-xl transform scale-[1.02] border border-white/20"
                            : "text-white/70 hover:bg-white/10 hover:text-white border border-transparent hover:border-white/10"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <div className={`flex-shrink-0 transition-all duration-300 ${isActive ? "text-white" : "text-primary group-hover:scale-110"
                              }`}>
                              <FontAwesomeIcon icon={getIconByText(getDisplayName(link.name))} className="w-6 h-6" />
                            </div>
                            <span className="text-sm font-semibold tracking-wide transition-all duration-300 ease-in-out translate-x-0 opacity-100 uppercase text-[11px] tracking-[0.1em]">
                              {getDisplayName(link.name)}
                            </span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  )
              )}
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default Sidebar;
export { SidebarContext, getIconByText, sidebarLinks };
